import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  LinkOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link, useSearchParams } from 'react-router-dom';
import {
  createTaobaoAuthorizationUrl,
  getTaobaoAuthorizationStatus,
  getTaobaoOrderSyncStatus,
  getTaobaoUserBindings,
  recheckTaobaoPublisherCapability,
  resetTaobaoUserBinding,
} from '../../api/taobaoIntegration';
import { getTaobaoCapabilities } from '../../api/systemConfig';

const { Text, Title } = Typography;

const STATUS_PRESENTATION = {
  UNAUTHORIZED: { label: '未授权', color: 'default' },
  ACTIVE: { label: '授权有效', color: 'success' },
  EXPIRING: { label: '即将过期', color: 'warning' },
  EXPIRED: { label: '已过期', color: 'error' },
  INVALID: { label: '已失效', color: 'error' },
};

const CALLBACK_NOTICES = {
  success: { type: 'success', message: 'OAuth 授权已完成' },
  denied: { type: 'warning', message: '淘宝授权已取消' },
  failed: { type: 'error', message: '淘宝授权未完成，请重新发起授权' },
};

const REFRESH_PRESENTATION = {
  AVAILABLE: { label: '自动续期可用', color: 'success' },
  RETRY_SCHEDULED: { label: '等待自动重试', color: 'warning' },
  REAUTH_REQUIRED: { label: '自动续期不可用', color: 'error' },
};

const CAPABILITY_PRESENTATION = {
  AVAILABLE: { label: '邀请码能力可用', color: 'success' },
  UPSTREAM_REJECTED: { label: '淘宝侧拒绝', color: 'warning' },
  PERMISSION_MISSING: { label: '接口权限缺失', color: 'error' },
  CHECK_FAILED: { label: '能力校验失败', color: 'warning' },
  UNKNOWN: { label: '能力待校验', color: 'default' },
};

const ORDER_SYNC_PRESENTATION = {
  READY: { label: '订单同步可用', color: 'success' },
  PERMISSION_MISSING: { label: '订单同步权限缺失', color: 'error' },
  FAILED: { label: '订单同步异常', color: 'warning' },
  UNKNOWN: { label: '订单同步待验证', color: 'default' },
  DISABLED: { label: '订单同步未启用', color: 'default' },
  UNAVAILABLE: { label: '订单状态不可用', color: 'warning' },
};

const BINDING_STATUS_PRESENTATION = {
  BOUND: { label: '已绑定', color: 'success' },
  PENDING: { label: '授权中', color: 'processing' },
  INVALID: { label: '已失效', color: 'error' },
};

const formatTime = (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '未提供';

const sourceLabel = (source) => {
  if (source === 'DATABASE') return '数据库加密凭证';
  if (source === 'ENVIRONMENT') return '环境变量兼容凭证';
  return '暂无凭证';
};

const TaobaoIntegration = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [orderSyncStatus, setOrderSyncStatus] = useState(null);
  const [runtimeCapabilities, setRuntimeCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorizing, setAuthorizing] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [bindingResult, setBindingResult] = useState({ list: [], total: 0 });
  const [bindingsLoading, setBindingsLoading] = useState(true);
  const [bindingError, setBindingError] = useState('');
  const [bindingPage, setBindingPage] = useState(1);
  const [bindingUserIdInput, setBindingUserIdInput] = useState('');
  const [bindingStatus, setBindingStatus] = useState('');
  const [appliedBindingUserId, setAppliedBindingUserId] = useState(undefined);
  const [resetTarget, setResetTarget] = useState(null);
  const [confirmedUserId, setConfirmedUserId] = useState('');
  const [resetReasonCode, setResetReasonCode] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetNotice, setResetNotice] = useState('');
  const bindingRequestIdRef = useRef(0);
  const callbackResult = searchParams.get('taobaoAuth');
  const callbackNotice = CALLBACK_NOTICES[callbackResult] || null;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [authorizationResult, orderSyncResult, capabilityResult] = await Promise.allSettled([
        getTaobaoAuthorizationStatus(),
        getTaobaoOrderSyncStatus(),
        getTaobaoCapabilities(),
      ]);
      if (authorizationResult.status === 'rejected') {
        throw authorizationResult.reason;
      }
      setStatus(authorizationResult.value);
      setOrderSyncStatus(orderSyncResult.status === 'fulfilled'
        ? orderSyncResult.value
        : { status: 'UNAVAILABLE', message: '订单同步状态加载失败' });
      setRuntimeCapabilities(capabilityResult.status === 'fulfilled' ? capabilityResult.value || [] : []);
    } catch (error) {
      setLoadError(error?.message || '淘宝授权状态加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const loadBindings = useCallback(async ({ page = 1, userId, status: nextStatus } = {}) => {
    const requestId = bindingRequestIdRef.current + 1;
    bindingRequestIdRef.current = requestId;
    setBindingsLoading(true);
    setBindingError('');
    try {
      const result = await getTaobaoUserBindings({
        page,
        size: 20,
        userId: userId || undefined,
        status: nextStatus || undefined,
      });
      if (requestId === bindingRequestIdRef.current) {
        setBindingResult(result || { list: [], total: 0 });
        setBindingPage(page);
      }
    } catch (error) {
      if (requestId === bindingRequestIdRef.current) {
        setBindingError(error?.message || '淘宝用户绑定加载失败');
      }
    } finally {
      if (requestId === bindingRequestIdRef.current) {
        setBindingsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadBindings({ page: 1 });
  }, [loadBindings]);

  const presentation = STATUS_PRESENTATION[status?.authorizationStatus] || STATUS_PRESENTATION.UNAUTHORIZED;
  const refreshPresentation = REFRESH_PRESENTATION[status?.refreshStatus]
    || { label: '未启用自动续期', color: 'default' };
  const capabilityPresentation = CAPABILITY_PRESENTATION[status?.publisherCapabilityStatus]
    || CAPABILITY_PRESENTATION.UNKNOWN;
  const orderSyncPresentation = ORDER_SYNC_PRESENTATION[orderSyncStatus?.status]
    || ORDER_SYNC_PRESENTATION.UNKNOWN;
  const items = useMemo(() => {
    const lifecycleItems = [
      { key: 'appKey', label: 'AppKey', children: status?.appKey || '未配置' },
      { key: 'adzoneId', label: '推广位', children: status?.adzoneId || '未配置' },
      { key: 'source', label: '凭证来源', children: sourceLabel(status?.credentialSource) },
      { key: 'account', label: 'OAuth 账号', children: status?.authorizedAccountHint || '未提供' },
      { key: 'authorizedAt', label: '最近授权', children: formatTime(status?.authorizedAt) },
      { key: 'expiresAt', label: '令牌到期', children: formatTime(status?.expiresAt) },
      { key: 'validatedAt', label: '最近验证', children: formatTime(status?.lastValidatedAt) },
      { key: 'lastRefreshedAt', label: '最近自动续期', children: formatTime(status?.lastRefreshedAt) },
      {
        key: 'publisherCapabilityCheckedAt',
        label: '邀请码能力校验',
        children: formatTime(status?.publisherCapabilityCheckedAt),
      },
    ];
    if (status?.nextRefreshAttemptAt) {
      lifecycleItems.push({
        key: 'nextRefreshAttemptAt',
        label: '下次自动重试',
        children: formatTime(status.nextRefreshAttemptAt),
      });
    }
    return lifecycleItems;
  }, [status]);

  const startAuthorization = async () => {
    setAuthorizing(true);
    setLoadError('');
    try {
      const response = await createTaobaoAuthorizationUrl();
      if (!response?.authorizationUrl) {
        throw new Error('未获取到淘宝授权链接');
      }
      window.open(response.authorizationUrl, '_self');
    } catch (error) {
      setLoadError(error?.message || '淘宝授权链接生成失败');
      setAuthorizing(false);
    }
  };

  const recheckPublisherCapability = async () => {
    setRechecking(true);
    setLoadError('');
    try {
      const nextStatus = await recheckTaobaoPublisherCapability();
      setStatus(nextStatus);
    } catch (error) {
      setLoadError(error?.message || '渠道能力重新校验失败');
    } finally {
      setRechecking(false);
    }
  };

  const searchBindings = () => {
    const trimmed = bindingUserIdInput.trim();
    const parsedUserId = /^\d+$/.test(trimmed) && Number(trimmed) > 0
      ? Number(trimmed)
      : undefined;
    if (trimmed && !parsedUserId) {
      setBindingError('请输入有效的 APP 用户 ID');
      return;
    }
    setAppliedBindingUserId(parsedUserId);
    loadBindings({ page: 1, userId: parsedUserId, status: bindingStatus });
  };

  const openResetDialog = (record) => {
    setResetTarget(record);
    setConfirmedUserId('');
    setResetReasonCode('');
    setResetNotice('');
  };

  const closeResetDialog = () => {
    if (resetting) return;
    setResetTarget(null);
    setConfirmedUserId('');
    setResetReasonCode('');
  };

  const submitReset = async () => {
    if (!resetTarget
      || Number(confirmedUserId) !== resetTarget.userId
      || !resetReasonCode) {
      setBindingError('请填写重置原因，并输入完整用户 ID 完成确认');
      return;
    }
    setResetting(true);
    setBindingError('');
    try {
      const response = await resetTaobaoUserBinding({
        bindingId: resetTarget.bindingId,
        userId: resetTarget.userId,
        confirmedUserId: Number(confirmedUserId),
        expectedUpdatedAt: resetTarget.updatedAt,
        expectedStatus: resetTarget.status,
        expectedConsentedAt: resetTarget.consentedAt || null,
        expectedVerifiedAt: resetTarget.verifiedAt || null,
        reasonCode: resetReasonCode,
      });
      setResetNotice(response?.message || '本地淘宝绑定已重置');
      setResetTarget(null);
      setConfirmedUserId('');
      setResetReasonCode('');
      await loadBindings({
        page: 1,
        userId: appliedBindingUserId,
        status: bindingStatus,
      });
    } catch (error) {
      setBindingError(error?.message || '本地淘宝绑定重置失败');
    } finally {
      setResetting(false);
    }
  };

  const bindingColumns = useMemo(() => [
    {
      title: 'APP 用户',
      key: 'user',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>用户 #{record.userId}</div>
          <Text type="secondary">{record.userHint || '用户资料不可用'}</Text>
        </div>
      ),
    },
    {
      title: '绑定状态',
      dataIndex: 'status',
      key: 'status',
      render: (value) => {
        const item = BINDING_STATUS_PRESENTATION[value] || { label: value || '未知', color: 'default' };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: '渠道关系',
      dataIndex: 'relationIdHint',
      key: 'relationIdHint',
      render: (value) => value || '尚未生成',
    },
    {
      title: '最近更新',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: formatTime,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button danger icon={<DeleteOutlined />} onClick={() => openResetDialog(record)}>
          重置本地绑定
        </Button>
      ),
    },
  ], []);

  const capabilityAlert = (() => {
    if (!status?.authorized || status?.publisherCapabilityStatus === 'AVAILABLE') return null;
    if (status?.publisherCapabilityStatus === 'UPSTREAM_REJECTED') {
      return {
        type: 'warning',
        message: '淘宝渠道能力被上游拒绝',
        description: 'OAuth 凭证已保存，但官方未公开该错误码的具体业务含义。无需重复授权，可重新校验或携带请求 ID 联系淘宝联盟核查。',
      };
    }
    if (status?.publisherCapabilityStatus === 'PERMISSION_MISSING') {
      return {
        type: 'error',
        message: '私域用户管理接口权限不可用',
        description: 'OAuth 凭证已保存，请核对 AppKey 的私域用户管理权限包后重新校验。',
      };
    }
    return {
      type: 'warning',
      message: '渠道邀请码能力尚未通过校验',
      description: 'OAuth 凭证已保存，无需重新授权。请稍后重新校验。',
    };
  })();

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
        <Space size={10}>
          <SafetyCertificateOutlined style={{ color: '#ff2442', fontSize: 22 }} />
          <Title level={2} style={{ margin: 0, fontSize: 22 }}>淘宝平台授权</Title>
        </Space>
        <Tooltip title="刷新授权状态">
          <Button
            aria-label="刷新授权状态"
            icon={<ReloadOutlined />}
            onClick={loadStatus}
            loading={loading}
          />
        </Tooltip>
      </div>

      {callbackNotice ? (
        <Alert
          showIcon
          type={callbackNotice.type}
          message={callbackNotice.message}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {loadError ? (
        <Alert showIcon type="error" message={loadError} style={{ marginBottom: 16 }} />
      ) : null}

      {loading && !status ? (
        <div style={{ padding: '72px 0', textAlign: 'center' }}><Spin size="large" /></div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
            <Tag color={presentation.color}>{presentation.label}</Tag>
            <Tag color={refreshPresentation.color}>{refreshPresentation.label}</Tag>
            <Tag color={capabilityPresentation.color}>{capabilityPresentation.label}</Tag>
            <Tag color={orderSyncPresentation.color}>{orderSyncPresentation.label}</Tag>
            <Text type="secondary">{status?.message || '尚未完成淘宝平台授权'}</Text>
          </div>

          <Alert
            showIcon
            type="info"
            message="淘宝接口运行策略"
            description={runtimeCapabilities.length > 0
              ? runtimeCapabilities.map((item) => `${item.displayName}：${item.effectiveEnabled ? '已开启' : '已关闭'}`).join(' · ')
              : '运行策略暂不可用，不影响当前授权状态查看。'}
            action={<Link to="/admin/system-config#taobao">前往系统配置</Link>}
            style={{ marginBottom: 18 }}
          />

          <Descriptions
            bordered
            size="middle"
            column={{ xs: 1, sm: 1, md: 2 }}
            items={items}
          />

          {status?.lastErrorCode ? (
            <Alert
              showIcon
              type="warning"
              message={`最近错误码：${status.lastErrorCode}`}
              style={{ marginTop: 16 }}
            />
          ) : null}

          {status?.lastRefreshErrorCode ? (
            <Alert
              showIcon
              type="warning"
              message={`最近自动续期错误码：${status.lastRefreshErrorCode}`}
              style={{ marginTop: 16 }}
            />
          ) : null}

          {capabilityAlert ? (
            <Alert
              showIcon
              type={capabilityAlert.type}
              message={capabilityAlert.message}
              description={`${capabilityAlert.description}${
                status?.publisherCapabilityErrorCode
                  ? ` 错误码：${status.publisherCapabilityErrorCode}。`
                  : ''
              }${
                status?.publisherCapabilityRequestId
                  ? ` 请求 ID：${status.publisherCapabilityRequestId}。`
                  : ''
              }`}
              style={{ marginTop: 16 }}
            />
          ) : null}

          {orderSyncStatus?.status === 'PERMISSION_MISSING' ? (
            <Alert
              showIcon
              type="error"
              message="淘宝订单同步权限缺失"
              description={`${orderSyncStatus.message || ''}${
                orderSyncStatus.lastErrorCode ? ` 错误码：${orderSyncStatus.lastErrorCode}。` : ''
              }`}
              style={{ marginTop: 16 }}
            />
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
            {status?.authorized && status?.publisherCapabilityStatus !== 'AVAILABLE' ? (
              <Button
                icon={<ReloadOutlined />}
                loading={rechecking}
                onClick={recheckPublisherCapability}
              >
                重新校验能力
              </Button>
            ) : null}
            <Button
              type="primary"
              icon={<LinkOutlined />}
              loading={authorizing}
              disabled={!status?.configured}
              onClick={startAuthorization}
            >
              {status?.authorized ? '重新授权' : '开始授权'}
            </Button>
          </div>

          <Divider style={{ margin: '28px 0 20px' }} />
          <div style={{ marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0, fontSize: 17 }}>用户授权测试</Title>
            <Text type="secondary">用于重复验证 APP 淘宝授权流程，仅影响本平台本地绑定。</Text>
          </div>

          <Alert
            showIcon
            type="warning"
            message="重置不会撤销淘宝联盟侧备案"
            description="操作会彻底删除本地绑定，尚未回流的旧订单将无法自动归因。用户下次购买时需要重新完成淘宝授权。"
            style={{ marginBottom: 16 }}
          />

          {bindingError ? (
            <Alert showIcon type="error" message={bindingError} style={{ marginBottom: 16 }} />
          ) : null}
          {resetNotice ? (
            <Alert showIcon type="success" message={resetNotice} style={{ marginBottom: 16 }} />
          ) : null}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <Input
              aria-label="APP用户ID"
              value={bindingUserIdInput}
              onChange={(event) => setBindingUserIdInput(event.target.value)}
              onPressEnter={searchBindings}
              placeholder="输入 APP 用户 ID"
              style={{ width: 220 }}
              inputMode="numeric"
            />
            <Select
              aria-label="绑定状态"
              value={bindingStatus}
              onChange={setBindingStatus}
              style={{ width: 150 }}
              options={[
                { value: '', label: '全部状态' },
                { value: 'BOUND', label: '已绑定' },
                { value: 'PENDING', label: '授权中' },
                { value: 'INVALID', label: '已失效' },
              ]}
            />
            <Button icon={<SearchOutlined />} onClick={searchBindings}>查询</Button>
          </div>

          <Table
            rowKey="bindingId"
            size="middle"
            loading={bindingsLoading}
            columns={bindingColumns}
            dataSource={bindingResult.list || []}
            locale={{ emptyText: <Empty description="暂无本地淘宝绑定" /> }}
            pagination={{
              current: bindingPage,
              pageSize: 20,
              total: bindingResult.total || 0,
              showSizeChanger: false,
              onChange: (nextPage) => loadBindings({
                page: nextPage,
                userId: appliedBindingUserId,
                status: bindingStatus,
              }),
            }}
          />

          <Modal
            open={Boolean(resetTarget)}
            title="确认重置本地淘宝绑定"
            okText="确认彻底重置"
            cancelText="取消"
            okButtonProps={{
              danger: true,
              disabled: !resetTarget
                || Number(confirmedUserId) !== resetTarget.userId
                || !resetReasonCode,
            }}
            confirmLoading={resetting}
            onOk={submitReset}
            onCancel={closeResetDialog}
            destroyOnHidden
          >
            <Alert
              showIcon
              type="error"
              message={`将彻底删除用户 #${resetTarget?.userId || ''} 的本地绑定`}
              description="该操作不可撤销，旧关系对应的延迟订单将无法自动归因。"
              style={{ marginBottom: 16 }}
            />
            <div style={{ marginBottom: 14 }}>
              <Text strong>重置原因</Text>
              <Select
                aria-label="重置原因"
                value={resetReasonCode}
                onChange={setResetReasonCode}
                placeholder="选择测试目的"
                options={[
                  { value: '', label: '请选择测试目的' },
                  { value: 'AUTH_FLOW_RETEST', label: '重复验证淘宝授权流程' },
                  { value: 'CALLBACK_TROUBLESHOOTING', label: '排查淘宝授权回调' },
                  { value: 'ACCOUNT_SWITCH_TEST', label: '测试切换淘宝账号' },
                ]}
                style={{ marginTop: 6 }}
              />
            </div>
            <div>
              <Text strong>输入用户 ID {resetTarget?.userId || ''} 确认</Text>
              <Input
                aria-label="确认用户ID"
                value={confirmedUserId}
                onChange={(event) => setConfirmedUserId(event.target.value)}
                placeholder="完整 APP 用户 ID"
                inputMode="numeric"
                style={{ marginTop: 6 }}
              />
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default TaobaoIntegration;
